import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    if (isHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/${href}`);
    }
  };

  const linkClass = (active = false) =>
    `text-sm tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap font-medium ${
      active
        ? 'text-amber-500 border-b-2 border-amber-400 pb-0.5'
        : scrolled || !isHome
        ? 'text-gray-600 hover:text-amber-600'
        : 'text-white/90 hover:text-amber-300'
    }`;

  const isProjectsActive = location.pathname === '/projeler' || location.pathname.startsWith('/projeler/');
  const isAboutActive = location.pathname === '/hakkimizda';
  const isContactActive = location.pathname === '/iletisim';

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled || !isHome
          ? 'bg-white/95 backdrop-blur-md border-b border-gray-100'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        {/* Logo */}
        <a
          href={isHome ? '#home' : '/'}
          onClick={(e) => {
            e.preventDefault();
            if (isHome) handleNavClick('#home');
            else navigate('/');
          }}
          className="flex items-center cursor-pointer"
        >
          <img
            src="https://okayisu.com/common/img/okayisu-logo.png"
            alt="OKAYISU Logo"
            className="h-12 w-auto object-contain"
            onError={(e) => {
              const target = e.currentTarget;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <span
            className="font-bold text-xl tracking-widest hidden"
            style={{ fontFamily: "'Montserrat', sans-serif", color: '#1a1a2e', display: 'none' }}
          >
            OKAYISU
          </span>
        </a>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-8">
          <li>
            <a
              href="#home"
              onClick={(e) => { e.preventDefault(); isHome ? handleNavClick('#home') : navigate('/'); }}
              className={linkClass(isHome && !isProjectsActive)}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {t('navbar.home')}
            </a>
          </li>
          <li>
            <a
              href="#services"
              onClick={(e) => { e.preventDefault(); handleNavClick('#services'); }}
              className={linkClass()}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {t('navbar.services')}
            </a>
          </li>
          <li>
            <Link
              to="/hakkimizda"
              className={linkClass(isAboutActive)}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {t('navbar.about')}
            </Link>
          </li>
          <li>
            <Link
              to="/projeler"
              className={linkClass(isProjectsActive)}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {t('navbar.projects')}
            </Link>
          </li>
          <li>
            <Link
              to="/iletisim"
              className={linkClass(isContactActive)}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {t('navbar.contact')}
            </Link>
          </li>

          {/* Language Toggle */}
          <li className="flex items-center gap-0.5 border border-gray-200 rounded-full p-0.5">
            <button
              onClick={() => i18n.changeLanguage('tr')}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                i18n.language === 'tr' || i18n.language?.startsWith('tr')
                  ? 'bg-amber-500 text-white'
                  : scrolled || !isHome
                  ? 'text-gray-500 hover:text-amber-600'
                  : 'text-white/70 hover:text-white'
              }`}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              TR
            </button>
            <button
              onClick={() => i18n.changeLanguage('en')}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                i18n.language === 'en' || i18n.language?.startsWith('en')
                  ? 'bg-amber-500 text-white'
                  : scrolled || !isHome
                  ? 'text-gray-500 hover:text-amber-600'
                  : 'text-white/70 hover:text-white'
              }`}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              EN
            </button>
          </li>
        </ul>

        {/* Hamburger */}
        <button
          className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${
            scrolled || !isHome ? 'text-gray-700' : 'text-white'
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white px-6 pb-6 border-t border-gray-100 shadow-lg w-full">
          <ul className="flex flex-col gap-1 pt-4">
            <li>
              <a
                href={isHome ? '#home' : '/'}
                onClick={(e) => {
                  e.preventDefault();
                  setMenuOpen(false);
                  if (isHome) handleNavClick('#home');
                  else navigate('/');
                }}
                className="text-gray-700 hover:text-amber-600 text-base tracking-wide transition-colors duration-200 cursor-pointer block py-2 font-medium"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {t('navbar.home')}
              </a>
            </li>
            <li>
              <a
                href="#services"
                onClick={(e) => { e.preventDefault(); handleNavClick('#services'); }}
                className="text-gray-700 hover:text-amber-600 text-base tracking-wide transition-colors duration-200 cursor-pointer block py-2 font-medium"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {t('navbar.services')}
              </a>
            </li>
            <li>
              <Link
                to="/hakkimizda"
                onClick={() => setMenuOpen(false)}
                className={`text-base tracking-wide transition-colors duration-200 cursor-pointer block py-2 font-medium ${
                  isAboutActive ? 'text-amber-500 font-bold' : 'text-gray-700 hover:text-amber-600'
                }`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {t('navbar.about')}
              </Link>
            </li>
            <li>
              <Link
                to="/projeler"
                onClick={() => setMenuOpen(false)}
                className={`text-base tracking-wide transition-colors duration-200 cursor-pointer block py-2 font-medium ${
                  isProjectsActive ? 'text-amber-500 font-bold' : 'text-gray-700 hover:text-amber-600'
                }`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {t('navbar.projects')}
              </Link>
            </li>
            <li>
              <Link
                to="/iletisim"
                onClick={() => setMenuOpen(false)}
                className={`text-base tracking-wide transition-colors duration-200 cursor-pointer block py-2 font-medium ${
                  isContactActive ? 'text-amber-500 font-bold' : 'text-gray-700 hover:text-amber-600'
                }`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {t('navbar.contact')}
              </Link>
            </li>

            {/* Language Toggle (Mobile) */}
            <li className="pt-3 mt-3 border-t border-gray-100 flex items-center gap-2">
              <span className="text-gray-400 text-xs font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>Language / Dil:</span>
              <button
                onClick={() => { i18n.changeLanguage('tr'); setMenuOpen(false); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer ${i18n.language === 'tr' || i18n.language?.startsWith('tr') ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-600'}`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                TR
              </button>
              <button
                onClick={() => { i18n.changeLanguage('en'); setMenuOpen(false); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer ${i18n.language === 'en' || i18n.language?.startsWith('en') ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-600'}`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                EN
              </button>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
}